-- | Canonical operation types shared by the app runtime and step flows.
module App.Operation.Types
    ( Generation
    , OperationId(..)
    , OperationKind(..)
    , OperationScope(..)
    , OperationStatus(..)
    , OperationEntry
    , operationLabel
    , operationScopes
    ) where

import Prelude

import API.Types (DocumentID)
import Data.Array as Arr
import Data.Maybe (Maybe(..), maybe)
import Error (ErrorWrapper)
import GraphQL.Type (innerId)

type Generation = Int

newtype OperationId = OperationId Int

derive newtype instance eqOperationId :: Eq OperationId
derive newtype instance ordOperationId :: Ord OperationId

data OperationKind
    = LoadRecentForms
    | SaveStep1FormOrder
    | CopyRecentForms (Array DocumentID)
    | DeleteRecentForms (Array DocumentID)
    | ExportRecentForms (Array DocumentID)
    | RequestRecentFormSignatures (Array DocumentID)
    | UpdateRecentForms (Array DocumentID)
    | LoadStep2Students
    | LoadStep2FormPicker
    | ExportStep2Forms
    | LoadStep3FormTypes
    | LoadStep3Document (Maybe DocumentID)
    | SaveStep3Document DocumentID
    | AutofillStep3Document DocumentID
    | ToggleStep3DocumentLock DocumentID
    | CreateStep3Comment DocumentID
    | UpdateStep3Comment DocumentID
    | DeleteStep3Comment DocumentID
    | CreateStep3Attachment DocumentID
    | DeleteStep3Attachment DocumentID
    | SignStep3Document DocumentID
    | SignStep3DocumentWithSaved DocumentID
    | RefreshStep3Signatures DocumentID
    | RequestStep3Signatures (Array DocumentID)
    | BulkToggleDocumentLocks (Array DocumentID)

data OperationScope
    = Step1GlobalScope
    | Step1RecentFormsScope
    | Step1FormOrderScope
    | Step1DocumentMutationScope
    | Step1ExportScope
    | Step2GlobalScope
    | Step2StudentSelectScope
    | Step2FormPickerScope
    | Step2ExportScope
    | Step3GlobalScope
    | Step3DocumentScope DocumentID
    | TransitionGenerationScope Generation

derive instance eqOperationScope :: Eq OperationScope
derive instance ordOperationScope :: Ord OperationScope

data OperationStatus
    = OperationRunning
    | OperationSucceeded
    | OperationFailed ErrorWrapper
    | OperationSuperseded

type OperationEntry =
    { id :: OperationId
    , kind :: OperationKind
    , scopes :: Array OperationScope
    , generation :: Maybe Generation
    , status :: OperationStatus
    }

operationLabel :: OperationKind -> String
operationLabel = case _ of
    LoadRecentForms -> "Loading recent forms"
    SaveStep1FormOrder -> "Saving form order"
    CopyRecentForms docIds -> "Copying " <> show (Arr.length docIds) <> " forms"
    DeleteRecentForms docIds -> "Deleting " <> show (Arr.length docIds) <> " forms"
    ExportRecentForms docIds -> "Exporting " <> show (Arr.length docIds) <> " forms"
    RequestRecentFormSignatures docIds -> "Requesting signatures for " <> show (Arr.length docIds) <> " forms"
    UpdateRecentForms docIds -> "Updating " <> show (Arr.length docIds) <> " forms"
    LoadStep2Students -> "Loading students"
    LoadStep2FormPicker -> "Loading form picker"
    ExportStep2Forms -> "Exporting selected forms"
    LoadStep3FormTypes -> "Loading form types"
    LoadStep3Document Nothing -> "Loading form"
    LoadStep3Document (Just docId) -> "Loading form " <> innerId docId
    SaveStep3Document docId -> "Saving form " <> innerId docId
    AutofillStep3Document docId -> "Autofilling form " <> innerId docId
    ToggleStep3DocumentLock docId -> "Updating lock for form " <> innerId docId
    CreateStep3Comment docId -> "Creating comment for form " <> innerId docId
    UpdateStep3Comment docId -> "Updating comment for form " <> innerId docId
    DeleteStep3Comment docId -> "Deleting comment for form " <> innerId docId
    CreateStep3Attachment docId -> "Creating attachment for form " <> innerId docId
    DeleteStep3Attachment docId -> "Deleting attachment for form " <> innerId docId
    SignStep3Document docId -> "Signing form " <> innerId docId
    SignStep3DocumentWithSaved docId -> "Signing form " <> innerId docId <> " with saved signature"
    RefreshStep3Signatures docId -> "Refreshing signatures for form " <> innerId docId
    RequestStep3Signatures docIds -> "Requesting signatures for " <> show (Arr.length docIds) <> " forms"
    BulkToggleDocumentLocks docIds -> "Updating " <> show (Arr.length docIds) <> " forms"

operationScopes :: OperationKind -> Maybe Generation -> Array OperationScope
operationScopes kind generation =
    baseScopes <> maybe [] (\gen -> [ TransitionGenerationScope gen ]) generation
  where
    baseScopes = case kind of
        LoadRecentForms -> [ Step1GlobalScope, Step1RecentFormsScope ]
        SaveStep1FormOrder -> [ Step1GlobalScope, Step1FormOrderScope ]
        CopyRecentForms _ -> [ Step1GlobalScope, Step1DocumentMutationScope ]
        DeleteRecentForms _ -> [ Step1GlobalScope, Step1DocumentMutationScope ]
        ExportRecentForms _ -> [ Step1GlobalScope, Step1ExportScope ]
        RequestRecentFormSignatures _ -> [ Step1GlobalScope, Step1DocumentMutationScope ]
        UpdateRecentForms _ -> [ Step1GlobalScope, Step1DocumentMutationScope ]
        LoadStep2Students -> [ Step2GlobalScope, Step2StudentSelectScope ]
        LoadStep2FormPicker -> [ Step2GlobalScope, Step2FormPickerScope ]
        ExportStep2Forms -> [ Step2GlobalScope, Step2ExportScope ]
        LoadStep3FormTypes -> [ Step3GlobalScope ]
        LoadStep3Document Nothing -> [ Step3GlobalScope ]
        LoadStep3Document (Just docId) -> [ Step3GlobalScope, Step3DocumentScope docId ]
        SaveStep3Document docId -> [ Step3GlobalScope, Step3DocumentScope docId ]
        AutofillStep3Document docId -> [ Step3GlobalScope, Step3DocumentScope docId ]
        ToggleStep3DocumentLock docId -> [ Step3GlobalScope, Step3DocumentScope docId ]
        CreateStep3Comment docId -> step3DocumentMutationScopes docId
        UpdateStep3Comment docId -> step3DocumentMutationScopes docId
        DeleteStep3Comment docId -> step3DocumentMutationScopes docId
        CreateStep3Attachment docId -> step3DocumentMutationScopes docId
        DeleteStep3Attachment docId -> step3DocumentMutationScopes docId
        SignStep3Document docId -> step3DocumentMutationScopes docId
        SignStep3DocumentWithSaved docId -> step3DocumentMutationScopes docId
        RefreshStep3Signatures docId -> step3DocumentMutationScopes docId
        RequestStep3Signatures docIds -> [ Step3GlobalScope ] <> map Step3DocumentScope (Arr.nub docIds)
        BulkToggleDocumentLocks _ -> [ Step1GlobalScope, Step1DocumentMutationScope ]

    step3DocumentMutationScopes docId =
        [ Step3GlobalScope, Step3DocumentScope docId ]
